import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';

import { BaseComponent } from '../../abstracts/base.abstract';
import { BasicInputTypeEnum } from '../../enums/basic-input-type.enum';
import { EntityTypeEnum } from '../../enums/entity-type.enum';
import { RepertoireFiltersEnum } from '../../enums/repertoire-filters.enum';
import { IQuestionChoices } from '../../interfaces/radio-checkbox-config.interface';
import { IRepertoireFilter } from '../../interfaces/repertoire.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { SetRepertoireFilter, SetRepertoireSearch } from '../../store/actions/repertoire.action';
import { AuthState } from '../../store/state/auth.state';
import { EnumsState } from '../../store/state/enums.state';
import { RepertoireState } from '../../store/state/repertoire.state';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
})
export class SearchComponent extends BaseComponent implements OnInit {
    @Select(EnumsState.educationLevels) public educationLevels$: Observable<IQuestionChoices[]>;
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;

    public basicInputTypeEnum = BasicInputTypeEnum;

    public entityTypeEnum = EntityTypeEnum;
    public entityTypesChoices: IQuestionChoices[];
    public entitiesTypeInitValues: string[] = [];
    public entitiesTypeCurrentValues: string[] = [];

    entityStatusChoices: IQuestionChoices[];

    public educationLevelInitValues: number[] = [];
    public educationLevelCurrentValues: number[] = [];

    public searchForm: FormGroup;
    public filterForm: FormGroup;

    public appliedFilters = false;
    public showFilters = false;

    constructor(private builder: FormBuilder, private store: Store) {
        super();
    }

    ngOnInit(): void {
        this.searchForm = this.builder.group({
            searchValue: [''],
        });

        this.filterForm = this.builder.group({
            educationLevel: [[]],
            entitiesTypes: [[]],
            entitiesStatus: [[]],
        });

        this.entityStatusChoices = [
            { value: 'active', label: 'REPERTOIRE.FORMS.STATUS.ACTIVE' },
            { value: 'deactivate', label: 'REPERTOIRE.FORMS.STATUS.DEACTIVATE' },
        ];

        this.uns = this.searchForm.valueChanges.subscribe(() => {
            this.search(this.searchValue.value);
        });

        this.uns = this.currentUser$.subscribe((user: IUser) => {
            this.entitiesTypeInitValues = [];
            this.entityTypesChoices = Object.keys(this.entityTypeEnum).map((entityType: string) => {
                this.entitiesTypeInitValues = [...this.entitiesTypeInitValues, entityType];
                return {
                    label: 'ENUM.ENTITY-TYPES.' + entityType,
                    value: entityType,
                };
            });
            this.entityTypesChoices = this.entityTypesChoices
                .filter(
                    (choice: IQuestionChoices) =>
                        choice.value !== 'GROUP_PERSO' &&
                        choice.value !== 'GROUP_PREDEFINED'
                )
                .sort((a: IQuestionChoices, b: IQuestionChoices) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));

            if (user && user.type === EntityTypeEnum.ADMINISTRATOR) {
                this.entityTypesChoices = this.entityTypesChoices
                    .filter(
                        (choice: IQuestionChoices) =>
                            choice.value !== 'STUDENT' &&
                            choice.value !== 'PARENT' &&
                            choice.value !== 'CLASS' &&
                            choice.value !== 'RELATIVE' &&
                            choice.value !== 'CONTACT' &&
                            choice.value !== 'GROUP_PERSO' &&
                            choice.value !== 'GROUP_PREDEFINED' &&
                            choice.value !== 'GROUP'
                    )
                    .sort((a: IQuestionChoices, b: IQuestionChoices) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));
                this.entitiesTypeInitValues = this.entitiesTypeInitValues
                    .filter(
                        (value: string) =>
                            value !== 'STUDENT' &&
                            value !== 'PARENT' &&
                            value !== 'GROUP_PERSO' &&
                            value !== 'GROUP_PREDEFINED' &&
                            value !== 'CLASS' &&
                            value !== 'RELATIVE' &&
                            value !== 'GROUP'
                    )
                    .sort((a: string, b: string) => (a > b ? 1 : b > a ? -1 : 0));
            }
            if (user && user.type === EntityTypeEnum.EMPLOYEE) {
                this.entityTypesChoices = this.entityTypesChoices.filter(
                    (choice: IQuestionChoices) =>
                        choice.value !== 'ADMINISTRATOR' &&
                        choice.value !== 'RELATIVE' &&
                        choice.value !== 'SCHOOL' &&
                        choice.value !== 'GROUP_PERSO' &&
                        choice.value !== 'GROUP_PREDEFINED'
                );
                this.entitiesTypeInitValues = this.entitiesTypeInitValues.filter(
                    (value: string) =>
                        value !== 'ADMINISTRATOR' &&
                        value !== 'RELATIVE' &&
                        value !== 'SCHOOL' &&
                        value !== 'GROUP_PERSO' &&
                        value !== 'GROUP_PREDEFINED'
                );
            }
            if (user && user.type === EntityTypeEnum.STUDENT) {
                this.entityTypesChoices = this.entityTypesChoices.filter(
                    (choice: IQuestionChoices) =>
                        choice.value !== 'ADMINISTRATOR' && choice.value !== 'SCHOOL' && choice.value !== 'GROUP_PREDEFINED'
                );
                this.entitiesTypeInitValues = this.entitiesTypeInitValues.filter(
                    (value: string) => value !== 'ADMINISTRATOR' && value !== 'SCHOOL' && value !== 'GROUP_PREDEFINED'
                );
            }
            if (user && user.type === EntityTypeEnum.PARENT) {
                this.entityTypesChoices = this.entityTypesChoices.filter(
                    (choice: IQuestionChoices) =>
                        choice.value !== 'ADMINISTRATOR' &&
                        choice.value !== 'SCHOOL' &&
                        choice.value !== 'GROUP_PREDEFINED' &&
                        choice.value !== 'GROUP'
                );
                this.entitiesTypeInitValues = this.entitiesTypeInitValues.filter(
                    (value: string) => value !== 'ADMINISTRATOR' && value !== 'SCHOOL' && value !== 'GROUP_PREDEFINED' && value !== 'GROUP'
                );
            }

            this.entitiesTypeCurrentValues = this.entitiesTypeInitValues;
            this.patchEntitiesTypes(this.entitiesTypeCurrentValues);
        });

        this.uns = this.educationLevels$.subscribe((edLevels: IQuestionChoices[]) => {
            edLevels.forEach((edLevel: IQuestionChoices) => {
                this.educationLevelInitValues = [...this.educationLevelInitValues, +edLevel.value];
                this.educationLevelCurrentValues = this.educationLevelInitValues;
            });
        });

        // SAVE INIT VALUES FOR FILTERS
        const edLevelCurrentFilter: IRepertoireFilter = this.store
            .selectSnapshot(RepertoireState.getRepertoireFilters)
            .find((filter: IRepertoireFilter) => filter.filterType === RepertoireFiltersEnum.EDUCATION_LEVEL);
        if (edLevelCurrentFilter) {
            this.educationLevelCurrentValues = edLevelCurrentFilter.filterValues;
        }

        const entitiesTypesCurrentFilter: IRepertoireFilter = this.store
            .selectSnapshot(RepertoireState.getRepertoireFilters)
            .find((filter: IRepertoireFilter) => filter.filterType === RepertoireFiltersEnum.ENTITY_TYPES);
        if (entitiesTypesCurrentFilter) {
            this.entitiesTypeCurrentValues = this.restoreEntityTypesValues(entitiesTypesCurrentFilter.filterValues);
        }

        const currentSearch: string = this.store.selectSnapshot(RepertoireState.getRepertoireSearchValue);
        if (currentSearch) {
            this.searchForm.get('searchValue').patchValue(currentSearch);
        }

        this.patchEducationLevels(this.educationLevelCurrentValues);
        this.patchEntitiesTypes(this.entitiesTypeCurrentValues);
        // END SAVE
        this.applyFilters();
    }

    public resetFilters(): void {
        this.showFilters = false;
        this.appliedFilters = false;
        this.store.dispatch(new SetRepertoireFilter([]));
        this.patchEducationLevels(this.educationLevelInitValues);
        this.patchEntitiesTypes(this.entitiesTypeInitValues);
    }

    public resetSearch(): void {
        this.searchForm.get('searchValue').patchValue('');
    }

    public applyFilters(): void {
        let filters: IRepertoireFilter[] = [];
        if (this.educationLevel.value.length) {
            filters = [
                ...filters,
                {
                    filterType: RepertoireFiltersEnum.EDUCATION_LEVEL,
                    filterValues: this.educationLevel.value,
                },
            ];
        }
        filters = [
            ...filters,
            {
                filterType: RepertoireFiltersEnum.ENTITY_TYPES,
                filterValues: this.getEntityTypesValues(this.entitiesTypes.value),
            },
        ];
        this.showFilters = false;
        this.appliedFilters = !!filters.length;
        this.store.dispatch(new SetRepertoireFilter(filters));
    }

    public search(searchValue: string): void {
        this.store.dispatch(new SetRepertoireSearch(searchValue));
    }

    public get educationLevel(): FormControl {
        return this.filterForm.get('educationLevel') as FormControl;
    }

    public get entitiesTypes(): FormControl {
        return this.filterForm.get('entitiesTypes') as FormControl;
    }

    public get searchValue(): FormControl {
        return this.searchForm.get('searchValue') as FormControl;
    }

    private patchEducationLevels(value: number[]): void {
        this.educationLevel.patchValue([...value]);
    }

    private patchEntitiesTypes(value: string[]): void {
        this.entitiesTypes.patchValue([...value]);
    }

    private getEntityTypesValues(values: any[]): any[] {
        if (values.find((v: any) => v.toLocaleLowerCase() === EntityTypeEnum.GROUP)) {
            values = [...values, 'GROUP_PERSO', 'GROUP_PREDEFINED'].filter((v: any) => v.toLocaleLowerCase() !== EntityTypeEnum.GROUP);
        }
        return values;
    }

    private restoreEntityTypesValues(values: any[]): any[] {
        if (
            values.find(
                (v: any) =>
                    v.toLocaleLowerCase() === EntityTypeEnum.GROUP_PERSO ||
                    v.toLocaleLowerCase() === EntityTypeEnum.GROUP_PREDEFINED
            )
        ) {
            values = [
                ...values.filter(
                    (v: any) =>
                        v.toLocaleLowerCase() !== EntityTypeEnum.GROUP_PERSO &&
                        v.toLocaleLowerCase() !== EntityTypeEnum.GROUP_PREDEFINED
                ),
                'GROUP',
            ];
        }
        return values;
    }
}
